import { FC, useEffect, useRef, useState } from 'react';
import { ContactAvatar } from '../../contact_avatar/ContactAvatar';
import { Col, Row } from 'antd';
import { ExportOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import * as React from 'react';
import { nullSafeGet, nullSafeGetOrElse } from '../../../utils/DataAccessUtils';
import Ellipsis from 'ant-design-pro/lib/Ellipsis';
import {
	getProtectedImageUriForBuyer,
	getProtectedImageUriForSupplier,
} from '../../../utils/FileAccessUtils';
import useOnScreen from '../../../hooks/onScreen';

interface LocationCardProps {
	location: any;
	name: any;
	subTitle?: string;
	showImages?: boolean;
	size?: 'default' | 'large';
	mode?: 'full' | 'simple' | 'avatar';
	details?: any;
	isVertical?: boolean;
	userType?: 'buyer' | 'supplier';
}

function formatPhoneNumber(phoneNumberString) {
	var cleaned = ('' + phoneNumberString).replace(/\D/g, '');
	var match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
	if (match) {
		var intlCode = match[1] ? '+1 ' : '';
		return [intlCode, '(', match[2], ') ', match[3], '-', match[4]].join('');
	}
	return null;
}

const REGION_DISPLAY = {
	key: 'region',
	display: 'Region',
	formatValue: (value) => value,
};
const FULL_ADDRESS_DISPLAY = {
	key: 'buyerFacility.primaryAddress',
	display: '',
	formatValue: (value) => (
		<>
			<div className="">{nullSafeGet('streetAddress1', value)}</div>
			{nullSafeGet('streetAddress2Opt', value) ? (
				<div className="">{nullSafeGet('streetAddress2Opt', value)}</div>
			) : null}
			<div className="">
				{nullSafeGet('city', value)}, {nullSafeGet('region', value)}{' '}
				{nullSafeGet('postcode', value)}
			</div>
		</>
	),
	lines: 2,
};
const CITY_DISPLAY = {
	key: 'buyerFacility.primaryAddress.city',
	display: 'City',
	formatValue: (value) => value,
};
const STATE_DISPLAY = {
	key: 'buyerFacility.primaryAddress.region',
	display: 'State',
	formatValue: (value) => value,
};
const CITY_STATE_DISPLAY = {
	key: 'buyerFacility.primaryAddress',
	display: 'City/State',
	formatValue: (value) => `${nullSafeGet('city', value)}, ${nullSafeGet('region', value)}`,
};

const STREET_ADDRESS_DISPLAY = {
	key: 'buyerFacility.primaryAddress.streetAddress1',
	display: 'Street Address',
	formatValue: (value) => value,
};

const ISACTIVE_DISPLAY = {
	key: 'isActive',
	display: 'Is Active',
	formatValue: (value) => (value ? 'true' : 'false'),
};
const STORE_ID_NAME_DISPLAY = {
	key: '',
	display: '',
	formatValue: (value) =>
		`${nullSafeGet('locationType.name', value)} ${nullSafeGet('storeId', value)}`,
};

export const LocationCard: FC<LocationCardProps> = ({
	location,
	name,
	subTitle,
	showImages = true,
	size = 'default',
	mode = 'simple',
	details = [STORE_ID_NAME_DISPLAY, FULL_ADDRESS_DISPLAY],
	isVertical = false,
	userType = 'buyer',
}) => {
	const lineHeight = '18.4';
	const getProtectedImageMethod =
		userType === 'supplier' ? getProtectedImageUriForSupplier : getProtectedImageUriForBuyer;
	const displayImageURL =
		location.images &&
		location.images[0] &&
		getProtectedImageMethod(
			location.images[0].split('/')[0],
			location.images[0].split('/')[1],
			'auto',
			120
		);

	const onClick = (e) => {
		e.preventDefault();
		e.stopPropagation();
	};

	const contentRef = useRef<HTMLDivElement>(null);
	const [contentHeight, setContentHeight] = useState<number | null>(null);
	useEffect(() => {
		if (!contentRef.current) return;
		const observer = new ResizeObserver((entries) => {
			for (const entry of entries) {
				const height = entry.contentRect.height;
				if (height > 0) setContentHeight(height);
			}
		});
		observer.observe(contentRef.current);
		return () => observer.disconnect();
	}, []);

	const detailList = details
		.filter((obj) => nullSafeGet(obj.key, location) !== null)
		.map((obj) => ({
			...obj,
			value: obj.hasOwnProperty('formatValue')
				? obj.formatValue(obj.key ? nullSafeGet(obj.key, location) : location)
				: nullSafeGet(obj.key, location),
		}));

	return (
		<span
			onClick={onClick}
			className={`flex ${isVertical ? 'flex-row gap-4' : 'flex-col gap-2'} flex-nowrap`}
		>
			{showImages && location.images && location.images.length > 0 && isVertical !== undefined ? (
				<div
					className={'flex flex-row'}
					style={{
						height: isVertical && contentHeight ? `${contentHeight}px` : 'auto',
						width: isVertical ? 'auto' : '100%',
						overflow: 'hidden',
						flexShrink: 0,
					}}
				>
					<img
						src={displayImageURL}
						style={{
							height: isVertical ? '100%' : 'auto',
							width: isVertical ? 'auto' : '100%',
							objectFit: 'contain',
						}}
					/>
				</div>
			) : null}
			<div ref={contentRef} className={'flex flex-col'} style={{ alignSelf: 'flex-start' }}>
				<div className={'flex flex-row'}>
					<div className={'flex flex-col'}>
						<Row
							style={{
								justifyContent: 'flex-start',
								color: 'rgba(0,0,0,0.65)',
								fontSize: size === 'large' || !subTitle ? 17.5 : 14,
								minWidth: `50px`,
							}}
						>
							<span
								style={{
									display: '-webkit-box',
									WebkitLineClamp: 3,
									WebkitBoxOrient: 'vertical',
									overflow: 'hidden',
									wordBreak: 'normal',
									overflowWrap: 'normal',
									whiteSpace: 'normal',
									maxWidth: '320px',
								}}
							>
								{name}
							</span>
						</Row>
						<Row
							style={{
								justifyContent: 'flex-start',
								color: 'rgba(0,0,0,0.45)',
								fontSize: size === 'large' ? 14 : 12,
							}}
						>
							<Ellipsis length={24}>{subTitle}</Ellipsis>
						</Row>
					</div>
				</div>
				{mode === 'full' && detailList.length > 0 ? (
					<Row
						style={{
							gap: 8,
							width: '100%',
							marginTop: 4,
						}}
					>
						<Col>
							{detailList.map((obj) => (
								<Row
									style={{
										display: 'flex',
										flexDirection: 'column',
										color: 'rgba(0,0,0,0.45)',
										fontSize: size === 'large' ? 14 : 12,
										alignItems: 'flex-start',
										lineHeight: lineHeight + 'px',
										wordBreak: 'normal',
										overflowWrap: 'normal',
										whiteSpace: 'normal',
									}}
								>
									{obj.value}
								</Row>
							))}
						</Col>
					</Row>
				) : null}
				{mode === 'full' && location.id ? (
					<Link
						to={
							userType === 'supplier' && nullSafeGet('contractorTenantId', location)
								? `/supplier/contractor_clients/locations/detail/${location.id}`
								: `/${userType}/locations/detail/${location.id}`
						}
						target="_blank"
						rel="noopener noreferrer"
						onClick={(e) => e.stopPropagation()}
						style={{
							marginTop: 4,
							display: 'inline-flex',
							alignItems: 'center',
							gap: 4,
							fontSize: size === 'large' ? 14 : 12,
						}}
					>
						Go to location
						<ExportOutlined translate="" />
					</Link>
				) : null}
			</div>
		</span>
	);
};
